import { useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, Grid3x3, Loader2, QrCode, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const LABELS_PER_SHEET = 24;
const MAX_LABELS = LABELS_PER_SHEET * 20;

/**
 * Skriv ut strekkode/QR for varer: hyllekort (A6, eitt per vare) eller etikettark (A4, 70×37 mm, 24 per ark).
 * QR = lenke til varesida i butikken, strekkode (Code 128) = varenummeret. PDF-en blir laga på serveren.
 */
export const LabelPrintDialog = ({ open, onOpenChange, products = [] }) => {
  const { t } = useLang();
  const [format, setFormat] = useState("shelf");
  const [count, setCount] = useState("4");
  const [grid, setGrid] = useState(true);
  const [busy, setBusy] = useState(""); // "open" | "download"

  useEffect(() => {
    if (open) setBusy("");
  }, [open]);

  const perProduct = Math.min(100, Math.max(1, parseInt(count, 10) || 1));
  const totalLabels = products.length * perProduct;
  const sheets = Math.ceil(totalLabels / LABELS_PER_SHEET);
  const withoutSku = useMemo(() => products.filter((p) => !p.sku).length, [products]);
  const tooMany = format === "sheet" && totalLabels > MAX_LABELS;

  const generate = async (mode) => {
    if (products.length === 0 || tooMany) return;
    setBusy(mode);
    try {
      const body = { format, grid, items: products.map((p) => ({ product_id: p.id, count: format === "sheet" ? perProduct : 1 })) };
      const res = await api.post("/my/shop/labels.pdf", body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const filename = format === "sheet" ? "butikk-etiketter.pdf" : "butikk-hyllekort.pdf";
      let opened = false;
      if (mode === "open") {
        const win = window.open(url, "_blank");
        opened = !!win;
      }
      if (!opened) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(format === "sheet" ? t("Etikettark er klart ({n} ark)", { n: sheets }) : t("Hyllekort er klare ({n} stk.)", { n: products.length }));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    } finally {
      setBusy("");
    }
  };

  const Option = ({ value, Icon, title, desc, testId }) => {
    const selected = format === value;
    return (
      <button
        type="button"
        onClick={() => setFormat(value)}
        aria-pressed={selected}
        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"}`}
        data-testid={testId}
      >
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${selected ? "bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]" : "bg-muted text-muted-foreground"}`}><Icon className="w-4 h-4" /></span>
        <span className="min-w-0">
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-xs text-muted-foreground">{desc}</span>
        </span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="label-print-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><QrCode className="w-5 h-5 text-muted-foreground" /> {t("Skriv ut strekkode og QR")}</DialogTitle>
          <DialogDescription>
            {products.length === 1
              ? t("Vare: {name}", { name: products[0].name })
              : t("{n} varer valgt", { n: products.length })}
            {" · "}{t("QR-koden lenker til varesiden i butikken, strekkoden (Code 128) er varenummeret.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t("Format")}>
            <Option value="shelf" Icon={Tag} title={t("Hyllekort (A6)")} desc={t("Ett kort per vare med navn, pris, stor QR og strekkode")} testId="label-format-shelf" />
            <Option value="sheet" Icon={Grid3x3} title={t("Etikettark (A4)")} desc={t("70 × 37 mm, 24 etiketter per ark – til poser og utlevering")} testId="label-format-sheet" />
          </div>

          {format === "sheet" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="label-count">{t("Etiketter per vare")}</Label>
                <Input id="label-count" type="number" min={1} max={100} inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} className="tabular-nums" data-testid="label-count-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Papir")}</Label>
                <label className="flex items-center gap-2 rounded-lg border border-border px-3 h-10 cursor-pointer">
                  <Checkbox checked={grid} onCheckedChange={(v) => setGrid(!!v)} data-testid="label-grid-checkbox" />
                  <span className="text-sm">{t("Vis skjærelinjer (vanlig papir)")}</span>
                </label>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground" data-testid="label-summary">
            {format === "sheet"
              ? t("{labels} etiketter på {sheets} ark", { labels: totalLabels, sheets })
              : t("{n} hyllekort (én side per vare)", { n: products.length })}
          </p>
          {tooMany && <p className="text-sm text-[hsl(var(--danger))]" role="alert" data-testid="label-too-many">{t("Maks {n} etiketter om gangen – reduser antall per vare.", { n: MAX_LABELS })}</p>}
          {withoutSku > 0 && (
            <p className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/40 p-3" data-testid="label-no-sku-notice">
              {t("{n} av varene mangler varenummer og får bare QR-kode. Lagre varen én gang i vareredigeringen, så får den et nummer automatisk.", { n: withoutSku })}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => generate("download")} disabled={!!busy || tooMany || products.length === 0} data-testid="label-download-button">
            {busy === "download" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} {t("Last ned PDF")}
          </Button>
          <Button type="button" onClick={() => generate("open")} disabled={!!busy || tooMany || products.length === 0} data-testid="label-open-button">
            {busy === "open" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1.5" />} {t("Åpne og skriv ut")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
